/**
 * Пришедшие с бэкенда данные об автомобилях нужно нормализовать - привести к виду, который потом можно использовать
 * в приложении в удобном и понятном виде. Кроме изменения структуры данных в процессе нормализации так же нужно сделать:
 *
 * Заменить плейсхолдеры
 * В настройки стейджа приходят, среди прочего, шаблоны для заголовка автомобиля, шаблоны для списка основных характеристик
 * (привод, двигатель, пробег), шаблоны для "года выпуска". Шаблон представляет из себя строку с плейсхолдерами, например
 * "Новая ${car?.Complectation} ${car?.marketingName}". Каждая пришедшая с бэкенда машина имеет в данных раздел placeholders,
 * содержащий значения плейсхолдеров, которые нужно подставить в шаблоны, чтобы получить финальное значение.
 *
 * Собрать список самих характеристик (замена плейсхолдеров и добавление иконки)
 *
 * Трансформировать пришедшие в автомобиле списки ID картинок в готовые к рендеру объекты, содержащие УРЛ картинки и её свойства
 *
 * Всё, что относится к этой нормализации содержится в этом файле.
 *
 */
import { RawCarData } from 'api/cars'
import { FilterValues } from 'application/api'
import { AnyBreakpoints, AppTheme as InternalAppTheme } from 'application/theme'
import { CarData, CarViewSettings } from 'features/car/types'
import { replacePlaceholders } from 'lib/placeholders'

import { createCarImages, createThumbsAdaptivitySettings } from './image'
import { createLocationDescription, createLocationPhone } from './location'
import { createPrices } from './price'
import { createSpecifications } from './specification'
import { createCarStatus, getCarAvailability } from './status'
import { createUrl } from './url'

/**
 * Нормализует пришедший с АПИ автомобиль в вид, который будет использован в приложении
 * @param car
 * @param carViewSettings
 * @param appTheme
 */
export const prepareCarData = (
  car: RawCarData,
  carViewSettings: CarViewSettings,
  appTheme: InternalAppTheme,
  filter?: FilterValues | null
): CarData => {
  const {
    id,
    price,
    price_with_discounts,
    url_detail,
    quantity,
    dealers,
    mileage,
    cities,
    logistic_status,
    status,
    marketingColors,
    labels,
    transmission,
    additional_options_cost,
    productType
  } = car
  const prices = createPrices({
    lower: price_with_discounts,
    higher: price,
    displayLowest: carViewSettings?.price.displayLowest
  })
  const title = replacePlaceholders(carViewSettings?.titleTemplate, car?.placeholders)
  return {
    id,
    generation: car?.generation,
    year: replacePlaceholders(carViewSettings?.yearTemplate, car?.placeholders),
    yearRAW: car?.year,
    title: title,
    baseSpecifications: createSpecifications(car, carViewSettings),
    url: createUrl(url_detail || '', filter),
    mileage,
    price: prices.higher,
    priceWithDiscounts: prices.lower,
    quantity,
    dealers,
    cities,
    transmission,
    status: createCarStatus({
      status,
      logisticStatus: logistic_status,
      quantity
    }),
    images: createCarImages(car, carViewSettings?.images, carViewSettings?.images.adaptive, appTheme, title),
    thumbImages: createCarImages(
      car,
      carViewSettings?.images,
      createThumbsAdaptivitySettings(carViewSettings),
      appTheme,
      title
    ),
    defaultImageUrl: `${carViewSettings?.images.cdnUrl}/media/image/${carViewSettings?.images.default}`,
    videos: car?.videos || [],
    labels,
    marketingColors,
    baseColor: car?.base_color,
    wheelDrive: car?.wheel_drive,
    body: car?.body,
    fuel: car?.fuel,
    location: createLocationDescription(car, carViewSettings) || '',
    locationsLength: carViewSettings?.showLocationAs === 'cities' ? car?.cities.length : car?.dealers.length,
    vin: replacePlaceholders(carViewSettings?.vinTemplate, car?.placeholders),
    vinRaw: car?.vin_raw,
    productType: car?.productType,
    locationPhone: createLocationPhone(car, carViewSettings),
    upholstery: car?.upholstery,
    enginePowerHorsePower: car?.enginePowerHorsePower,
    collapsedCars: car?.collapsedCars,
    tradeInDiscount: car?.tradeinDiscount,
    creditTradeInDiscount: car?.creditTradeinDiscount,
    creditDiscount: car?.creditDiscount,
    insuranceDiscount: car?.insuranceDiscount,
    brand: {
      id: car?.brand.id,
      name: car?.brand.name
    },
    model: {
      id: car?.model.id,
      name: car?.model.name
    },
    available: getCarAvailability(status),
    marketingComplectation: {
      code: car?.marketing_complectation.code,
      id: car?.marketing_complectation.id
    },
    complectationId: car?.complectation.id,
    restyling: car?.restyling,
    additionalEquipmentCost: additional_options_cost,
    hideDealer: carViewSettings?.hideDealer,
    hideStatus: carViewSettings?.hideStatus,
    creditPayment: car?.credit_info?.monthly_payment || null,
    has_video: car?.has_video,
    reportVIN: car?.reportVIN,
    has_autoteka_report: car?.has_autoteka_report
  }
}
